import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Mic, MicOff, Send, Volume2, Loader2, MessageCircle,
  DollarSign, TrendingUp, AlertTriangle, Lightbulb, BarChart3
} from 'lucide-react';
import { toast } from 'sonner';
import { useDemoData } from '../components/useDemoData';
import EmptyDataState from '../components/EmptyDataState';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VoiceAssistant = () => {
  const { shouldShowData } = useDemoData();

  const { token } = useAuth();
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [conversation, setConversation] = useState([]);
  const [supportedIntents, setSupportedIntents] = useState(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchSupportedIntents();
  }, [token]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversation]);

  const fetchSupportedIntents = async () => {
    try {
      const response = await fetch(`${API_URL}/api/voice/supported-intents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setSupportedIntents(data);
    } catch (error) {
      console.error('Failed to fetch intents');
    }
  };

  const processQuery = async (text) => {
    if (!text.trim()) return;

    const userMessage = { type: 'user', text, timestamp: new Date() };
    setConversation(prev => [...prev, userMessage]);
    setQuery('');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/voice/query`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ transcript: text })
      });

      const data = await response.json();
      
      const assistantMessage = {
        type: 'assistant',
        text: data.response.spoken_response,
        intent: data.intent,
        confidence: data.confidence,
        data: data.response.data,
        followups: data.suggested_followups,
        timestamp: new Date()
      };
      
      setConversation(prev => [...prev, assistantMessage]);
      
      // Speak the response (if browser supports it)
      if ('speechSynthesis' in window) {
        const utterance = new SpeechSynthesisUtterance(data.response.spoken_response);
        utterance.rate = 1.0;
        utterance.pitch = 1.0;
        window.speechSynthesis.speak(utterance);
      }
    } catch (error) {
      toast.error('Failed to process query');
      setConversation(prev => [...prev, {
        type: 'assistant',
        text: "I'm sorry, I couldn't process your request. Please try again.",
        timestamp: new Date()
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      processQuery(query);
    }
  };

  const toggleListening = () => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      toast.error('Speech recognition not supported in this browser');
      return;
    }

    if (isListening) {
      setIsListening(false);
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = 'en-US';

    recognition.onstart = () => {
      setIsListening(true);
      toast.info('Listening...');
    };

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setQuery(transcript);
      processQuery(transcript);
    };

    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      toast.error('Speech recognition failed');
      setIsListening(false);
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    recognition.start();
  };

  const getIntentIcon = (intent) => {
    switch (intent) {
      case 'cost_summary': return <DollarSign className="w-4 h-4" />;
      case 'cost_by_service': return <BarChart3 className="w-4 h-4" />;
      case 'anomaly_check': return <AlertTriangle className="w-4 h-4" />;
      case 'recommendations': return <Lightbulb className="w-4 h-4" />;
      case 'forecast': return <TrendingUp className="w-4 h-4" />;
      case 'small_talk': return <MessageCircle className="w-4 h-4" />;
      case 'unknown': return <MessageCircle className="w-4 h-4" />;
      default: return <MessageCircle className="w-4 h-4" />;
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
  };

  if (!shouldShowData) {
    return <EmptyDataState title="Voice Assistant" description="Connect your cloud account to see real data here." />;
  }


  return (
    <div className="space-y-6" data-testid="voice-assistant">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
          <Mic className="w-7 h-7 text-emerald-500" />
          Voice-Controlled FinOps
        </h1>
        <p className="text-slate-400 mt-1">Ask questions about your cloud costs using natural language</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Chat Area */}
        <div className="lg:col-span-2">
          <Card className="h-[600px] flex flex-col">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2">
                <MessageCircle className="w-5 h-5" />
                CARTIEAI Assistant
              </CardTitle>
            </CardHeader>
            <CardContent className="flex-1 flex flex-col overflow-hidden">
              {/* Messages */}
              <div className="flex-1 overflow-y-auto space-y-4 mb-4">
                {conversation.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-slate-400">
                    <Volume2 className="w-16 h-16 mb-4" />
                    <p className="text-lg font-medium">Start a conversation</p>
                    <p className="text-sm">Ask about your cloud costs, anomalies, or recommendations</p>
                  </div>
                ) : (
                  conversation.map((msg, idx) => (
                    <div key={idx} className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                        msg.type === 'user' 
                          ? 'bg-emerald-500 text-white' 
                          : 'bg-slate-100 dark:bg-slate-800'
                      }`}>
                        <p>{msg.text}</p>
                        
                        {/* Show data visualization for assistant responses */}
                        {msg.type === 'assistant' && msg.data && Object.keys(msg.data).length > 0 && (
                          <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-700">
                            {msg.data.total && (
                              <div className="text-2xl font-bold text-emerald-600">
                                {formatCurrency(msg.data.total)}
                              </div>
                            )}
                            {msg.data.aws && msg.data.azure && msg.data.gcp && (
                              <div className="grid grid-cols-4 gap-2 mt-2 text-sm">
                                <div className="text-center p-2 bg-orange-100 dark:bg-orange-900/30 rounded">
                                  <p className="font-medium">AWS</p>
                                  <p>{formatCurrency(msg.data.aws)}</p>
                                </div>
                                <div className="text-center p-2 bg-emerald-100 dark:bg-blue-900/30 rounded">
                                  <p className="font-medium">Azure</p>
                                  <p>{formatCurrency(msg.data.azure)}</p>
                                </div>
                                <div className="text-center p-2 bg-red-100 dark:bg-red-900/30 rounded">
                                  <p className="font-medium">GCP</p>
                                  <p>{formatCurrency(msg.data.gcp)}</p>
                                </div>
                                <div className="text-center p-2 bg-emerald-100 dark:bg-cyan-900/30 rounded">
                                  <p className="font-medium">Snowflake</p>
                                  <p>{formatCurrency(msg.data.snowflake || 0)}</p>
                                </div>
                              </div>
                            )}
                            {/* v34.43 — example queries for small_talk / clarify intents */}
                            {Array.isArray(msg.data.examples) && msg.data.examples.length > 0 && (
                              <div className="space-y-1.5" data-testid="voice-examples">
                                <p className="text-xs text-slate-500 mb-1">Try one of these:</p>
                                {msg.data.examples.map((ex, i) => (
                                  <button
                                    key={i}
                                    type="button"
                                    onClick={() => processQuery(ex)}
                                    className="block text-left text-xs text-emerald-600 hover:underline"
                                    data-testid={`voice-example-${i}`}
                                  >
                                    "{ex}"
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* Intent badge */}
                        {msg.intent && (
                          <div className="flex items-center gap-2 mt-2">
                            <Badge variant="outline" className="text-xs">
                              {getIntentIcon(msg.intent)}
                              <span className="ml-1">{msg.intent.replace('_', ' ')}</span>
                            </Badge>
                            <span className="text-xs text-slate-400">
                              {Math.round(msg.confidence * 100)}% confident
                            </span>
                          </div>
                        )}
                        
                        {/* Follow-up suggestions */}
                        {msg.followups && (
                          <div className="mt-3 space-y-1">
                            <p className="text-xs text-slate-400">Try asking:</p>
                            {msg.followups.map((followup, i) => (
                              <button
                                key={i}
                                onClick={() => processQuery(followup)}
                                className="block text-left text-xs text-emerald-600 hover:underline"
                              >
                                "{followup}"
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input Area */}
              <div className="flex items-center gap-2 pt-3 border-t">
                <Button
                  variant={isListening ? "destructive" : "outline"}
                  size="icon"
                  onClick={toggleListening}
                  className={isListening ? 'animate-pulse' : ''}
                >
                  {isListening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                </Button>
                <Input
                  placeholder="Ask about your cloud costs..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyPress={handleKeyPress}
                  disabled={loading}
                  className="flex-1"
                />
                <Button onClick={() => processQuery(query)} disabled={loading || !query.trim()}>
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* Quick Questions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Quick Questions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {supportedIntents?.examples?.map((example, idx) => (
                <Button
                  key={idx}
                  variant="outline"
                  className="w-full justify-start text-left h-auto py-2 px-3"
                  onClick={() => processQuery(example)}
                >
                  <MessageCircle className="w-4 h-4 mr-2 flex-shrink-0" />
                  <span className="text-sm">{example}</span>
                </Button>
              ))}
            </CardContent>
          </Card>

          {/* Supported Intents */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">What I Can Help With</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {supportedIntents?.intents?.map((intent, idx) => (
                  <Badge key={idx} variant="secondary" className="capitalize">
                    {intent.replace('_', ' ')}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Tips */}
          <Card className="bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800">
            <CardHeader>
              <CardTitle className="text-sm text-emerald-700 dark:text-emerald-400">Tips</CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-emerald-600 dark:text-emerald-300 space-y-2">
              <p>• Click the microphone button to speak your question</p>
              <p>• Be specific: "AWS costs this month" or "Snowflake spend" vs just "costs"</p>
              <p>• Ask follow-up questions for more details</p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default VoiceAssistant;
